import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  useBuyerQuestions,
  useDraftAnswer,
  useRoomDocuments,
  useSellerQuestions,
  useSellerRoomAccess,
} from '@/api/hooks';
import {
  AnswerComposer,
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  ErrorText,
  HelpTip,
  LinkedDocumentList,
  PublishedQuestionList,
  cn,
  formatDate,
  type AnswerQuestionRequest,
  type ChipTone,
  type IconName,
  type LinkableDocument,
  type QuestionView,
} from '@dataroom/shared';

/**
 * "Questions for you" (ADR-0039). In a sell-side room: buyer questions the deal
 * intermediary sent to the seller. The seller drafts an answer — text plus
 * documents from their library — and the intermediary reviews and releases it
 * to the buyer. Who asked is not shown (the API does not send it).
 *
 * In a BUYER-RUN room (`mode: 'buy_side'`) the buyer runs the room and asks the
 * seller directly: each question shows the buyer firm (never the individual on
 * the buyer's team), and the seller's answer goes straight to the buyer.
 */
export function QuestionsPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <QuestionsPageInner roomId={access.roomId} />;
}

/** Where a routed question stands, from the seller's side. */
type SellerQuestionState = 'needs_answer' | 'waiting' | 'released' | 'closed';

function sellerQuestionState(q: QuestionView): SellerQuestionState {
  if (q.status === 'closed') return 'closed';
  if (q.answer?.released) return 'released';
  if (q.answer) return 'waiting';
  return 'needs_answer';
}

const STATE_CHIP: Record<SellerQuestionState, { label: string; tone: ChipTone; icon: IconName }> = {
  needs_answer: { label: 'Needs your answer', tone: 'check', icon: 'help' },
  waiting: { label: 'Waiting for release', tone: 'processing', icon: 'eye' },
  released: { label: 'Sent to the buyer', tone: 'certified', icon: 'check' },
  closed: { label: 'Closed', tone: 'na', icon: 'archive' },
};

function QuestionsPageInner({ roomId }: { roomId: string }) {
  const questions = useSellerQuestions(roomId);
  const documents = useRoomDocuments(roomId);
  const draft = useDraftAnswer(roomId);
  const shared = useBuyerQuestions(roomId);

  if (questions.error instanceof ApiError && questions.error.status === 401) {
    return <Navigate to="/login" replace />;
  }

  const buySide = questions.data?.mode === 'buy_side';
  const list = questions.data?.questions ?? [];
  const library: LinkableDocument[] = (documents.data ?? []).map((d) => ({
    id: d.id,
    name: d.name,
    categoryPath: d.categoryPath,
  }));

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Questions for you</CardTitle>
          <p className="text-sm text-muted-foreground">
            {buySide
              ? 'Questions from the buyer. Answer and link any documents that help — the buyer sees your answer as soon as you send it.'
              : 'Buyer questions the deal intermediary sent you. Draft an answer and link any documents that help — the intermediary reviews it before the buyer sees it.'}
          </p>
        </CardHeader>
        <CardContent>
          {questions.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : questions.error ? (
            <ErrorText error={questions.error} />
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">No questions for you right now.</p>
          ) : (
            <ul className="space-y-3">
              {list.map((q) => {
                const mine = draft.variables?.questionId === q.id;
                return (
                  <SellerQuestionItem
                    key={q.id}
                    roomId={roomId}
                    question={q}
                    buySide={buySide}
                    library={library}
                    pending={mine && draft.isPending}
                    error={mine && !draft.isPending ? draft.error : null}
                    onSubmit={(answer, onDone) =>
                      draft.mutate({ questionId: q.id, answer }, { onSuccess: onDone })
                    }
                    onReset={() => draft.reset()}
                  />
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {shared.data?.available && shared.data.questions.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Buyer Questions</CardTitle>
            <p className="text-sm text-muted-foreground">
              Questions and answers the deal intermediary has shared with every buyer who can see
              the CIM. Read-only.
            </p>
          </CardHeader>
          <CardContent>
            <PublishedQuestionList questions={shared.data.questions} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function SellerQuestionItem({
  roomId,
  question: q,
  buySide,
  library,
  pending,
  error,
  onSubmit,
  onReset,
}: {
  roomId: string;
  question: QuestionView;
  buySide: boolean;
  library: LinkableDocument[];
  pending: boolean;
  error: unknown;
  onSubmit: (answer: AnswerQuestionRequest, onDone: () => void) => void;
  onReset: () => void;
}) {
  const state = sellerQuestionState(q);
  const chip =
    buySide && state === 'released'
      ? { ...STATE_CHIP.released, label: 'Answered' }
      : STATE_CHIP[state];
  const [composing, setComposing] = useState(false);
  const answer = q.answer;
  // Buyer-run: the seller's answer is theirs to correct until the buyer closes the question.
  const editable = buySide ? state !== 'closed' : state === 'needs_answer' || state === 'waiting';

  return (
    <li className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1">
          <Chip tone={chip.tone} icon={chip.icon}>
            {chip.label}
          </Chip>
          <HelpTip id="seller.questions.status-chip" />
        </span>
        {buySide ? (
          <span className="text-xs font-medium text-ink-2">
            From {q.askedByFirm || 'the buyer'}
          </span>
        ) : null}
        <span className="text-xs text-muted-foreground">Asked {formatDate(q.createdAt)}</span>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm">{q.body}</p>
      {q.document || q.category ? (
        <p className="text-xs text-muted-foreground">
          About: {q.document ? q.document.name || 'Untitled document' : q.category?.name}
        </p>
      ) : null}

      {answer && !composing ? (
        <div
          className={cn(
            'space-y-2 rounded-md p-3',
            answer.released ? 'bg-surface-2' : 'border border-dashed border-line-strong',
          )}
        >
          <p className="text-xs font-semibold text-ink-2">
            {buySide
              ? 'Your answer, sent to the buyer'
              : answer.released
                ? 'The answer the buyer received'
                : 'Your draft — waiting for the intermediary to release it'}
            {answer.answeredAt ? ` · ${formatDate(answer.answeredAt)}` : ''}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm">{answer.body}</p>
          <LinkedDocumentList documents={answer.documents} />
        </div>
      ) : null}

      {editable ? (
        composing ? (
          <AnswerComposer
            suggestAside={<HelpTip id="seller.questions.suggest-documents" />}
            roomId={roomId}
            questionId={q.id}
            library={library}
            initial={answer ? { body: answer.body, documents: answer.documents } : null}
            label="Your answer"
            submitLabel={
              buySide
                ? answer
                  ? 'Update answer'
                  : 'Send answer'
                : answer
                  ? 'Save draft'
                  : 'Send for review'
            }
            hint={
              buySide
                ? 'The buyer’s team sees it at once — each person sees only the documents they can open.'
                : 'The intermediary reviews it before the buyer sees it.'
            }
            pending={pending}
            error={error}
            onSubmit={(a) => onSubmit(a, () => setComposing(false))}
            onCancel={() => {
              onReset();
              setComposing(false);
            }}
          />
        ) : (
          <Button
            size="sm"
            variant={answer ? 'outline' : 'default'}
            onClick={() => {
              onReset();
              setComposing(true);
            }}
          >
            {buySide
              ? answer
                ? 'Edit answer'
                : 'Answer'
              : answer
                ? 'Edit draft'
                : 'Draft an answer'}
          </Button>
        )
      ) : null}
    </li>
  );
}
